import { FC, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { logout } from '@features/auth';
import { useDirection,useInjection } from '@hooks';
import { Trans, useTrans } from '@libs/i18n';
import { DotsThreeIcon, GearSixIcon, SignOutIcon, StackIcon } from '@phosphor-icons/react';
import { LocalStorageStore } from '@stores';
import { NoFlipBox } from '@utils';

import { DI_TOKENS } from '../../constants';

export const ProfilePanel: FC = observer(() => {
  const { dir, isRtl } = useDirection();
  const { t } = useTrans();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const open = Boolean(anchorEl);
  const localStorageStore = useInjection<LocalStorageStore>(DI_TOKENS.LocalStorageStore);
  const navigate = useNavigate();

  const userProfile = localStorageStore.userProfile;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    // Show a spinner while the logout request settles before navigating away.
    // Navigating to /login immediately (before the request resolves) has caused
    // issues, so we keep the user here with clear feedback until it completes.
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logout();
      handleClose();
      // Force URL to settle on /login. Without this, the router-instance swap
      // in AppRouter can leave the URL on whatever protected page the user
      // logged out from, which then becomes a stale redirect_uri if they
      // click an SSO button.
      navigate('/login', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <NoFlipBox
      mode={dir}
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        backgroundColor: '#fff',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
      }}>
        <Avatar
          src={userProfile?.user?.image || '/Logo.png'}
          alt="avatar"
          sx={{
            width: 32,
            height: 32,
            marginInlineEnd: 1.25,
          }}
        />
        <Box>
          <Typography variant="body2" fontWeight="700" fontSize="1rem">
            {userProfile?.user?.firstName && userProfile?.user?.lastName
              ? `${userProfile?.user?.firstName} ${userProfile?.user?.lastName}`
              : userProfile?.user?.firstName || userProfile?.user?.lastName || t('auth.profilePanel.user.fallback')}
          </Typography>
          <Typography variant="body2" color="#858585" fontWeight="400" fontSize="0.875rem">
            {userProfile?.user?.email || t('auth.profilePanel.email.fallback')}
          </Typography>
        </Box>
      </Box>

      <IconButton
        onClick={handleClick}
        aria-controls={open ? 'profile-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <DotsThreeIcon size={24} color="text.primary" />
      </IconButton>

      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: isRtl ? 'left' : 'right',
        }}
        disableScrollLock
        transformOrigin={{
          vertical: 'bottom',
          horizontal: isRtl ? 'left' : 'right',
        }}
        sx={{
          '.MuiPaper-root': {
            width: containerRef.current ? `${containerRef.current.clientWidth + 48}px` : '280px',
            borderRadius: 3,
            boxShadow: '0px 4px 25px rgba(0, 0, 0, 0.15)',
            mb: 2.5,
            mr: isRtl ? 0 : -3,
            ml: isRtl ? -3 : 0,
            overflow: 'visible',
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              bottom: -6,
              right: isRtl ? 'auto' : 40,
              left: isRtl ? 40 : 'auto',
              width: 12,
              height: 12,
              bgcolor: 'background.paper',
              transform: 'rotate(45deg)',
              zIndex: 0,
              borderBottom: '1px solid',
              borderRight: '1px solid',
              borderColor: 'rgba(0,0,0,0.05)',
            },
          },
        }}
      >
        <MenuItem onClick={() => {
          handleClose();
          navigate('/tenant-profile');
        }}>
          <ListItemIcon>
            <StackIcon size={24} />
          </ListItemIcon>
          <ListItemText>
            <Trans i18nKey="search.panel.button.organizationHub" />
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          handleClose();
          navigate('/settings');
        }}>
          <ListItemIcon>
            <GearSixIcon size={24} />
          </ListItemIcon>
          <ListItemText>
            <Trans i18nKey="auth.profilePanel.button.settings" />
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogout} disabled={isLoggingOut}>
          <ListItemIcon>
            {isLoggingOut ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SignOutIcon
                size={24}
                style={{
                  transform: isRtl ? 'scaleX(-1)' : 'none',
                }}
              />
            )}
          </ListItemIcon>
          <ListItemText sx={{ fontSize: '0.875rem', fontWeight: '500' }}>
            <Trans i18nKey="auth.profilePanel.button.logout" />
          </ListItemText>
        </MenuItem>
      </Menu>
    </NoFlipBox>
  );
});
